import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs, onSnapshot, orderBy, limit, Timestamp } from "firebase/firestore";
import { db } from "../../firebase.js";
import Cookies from "js-cookie";
import { Toast } from '../../components_root/ToastContext.jsx';
import { 
    MdClose, MdSearch, MdFileDownload, MdArrowBackIos, MdCheckCircle, 
    MdWarning, MdReceiptLong, MdExpandMore, MdExpandLess, MdTrendingUp, 
    MdTrendingDown, MdInventory, MdAccountBalanceWallet, MdViewWeek, MdCalendarMonth, MdHelpOutline, MdBuild 
} from "react-icons/md";
import { TbReport } from "react-icons/tb";

export const seo = {
    title: "Balance Sheet & Income Statement",
    description: "View assets, receivables, inventory, liabilities, receipt incomes, expenses, monthly balance sheets, and collation matching",
    keywords: "balance sheet, monthly balance sheet, financial statement, accounting, assets, liabilities, equity, accounts receivable, inventory, income, expenses, collation audit",
    url: "https://aooz.in/balance-sheet",
    image: "https://aooz.in/assets/logo.png"
};

export default function Balance_sheet() {
    const [isLoading, setIsLoading] = useState(false);
    const { Toastshow } = Toast();
    const navigate = useNavigate();

    // View Mode: 'single' (Single Period) | 'monthly' (Monthly Breakdown)
    const [viewMode, setViewMode] = useState("single");

    // Option to overlay POS Receipts Summary
    const [includePosReceipts, setIncludePosReceipts] = useState(true);

    // Date Range (From & To)
    const [from_date, setFrom_date] = useState("");
    const [to_date, setTo_date] = useState("");

    const servicetablename = "accountingfinancedb";
    const [select_company_uid, setSelect_company_uid] = useState(null);
    const [guid, setGuid] = useState(null);
    
    // Categorized Assets
    const [receivableAssets, setReceivableAssets] = useState([]);
    const [inventoryAssets, setInventoryAssets] = useState([]);
    const [otherAssets, setOtherAssets] = useState([]);
    
    // Balance Sheet Totals
    const [liabilities, setLiabilities] = useState([]);
    const [equity, setEquity] = useState([]);
    const [incomeAccounts, setIncomeAccounts] = useState([]);
    const [expenseAccounts, setExpenseAccounts] = useState([]);
    const [unclassifiedAccounts, setUnclassifiedAccounts] = useState([]);
    
    const [totalReceivables, setTotalReceivables] = useState(0);
    const [totalInventory, setTotalInventory] = useState(0);
    const [totalOtherAssets, setTotalOtherAssets] = useState(0);
    const [totalAssets, setTotalAssets] = useState(0);
    
    const [totalLiabilities, setTotalLiabilities] = useState(0);
    const [totalEquityAccounts, setTotalEquityAccounts] = useState(0);
    
    // Income & Expense (Period & Cumulative)
    const [totalIncome, setTotalIncome] = useState(0);
    const [totalExpense, setTotalExpense] = useState(0);
    const [netProfit, setNetProfit] = useState(0);
    const [priorNetProfit, setPriorNetProfit] = useState(0);
    const [cumNetProfit, setCumNetProfit] = useState(0);

    // Monthly Comparative Data
    const [monthlyBreakdown, setMonthlyBreakdown] = useState([]);
    
    // POS Receipt & Breakdown UI States
    const [voucherSummary, setVoucherSummary] = useState([]);
    const [totalCredit, setTotalCredit] = useState(0);
    const [showPosDetails, setShowPosDetails] = useState(false);
    const [showIncomeDetails, setShowIncomeDetails] = useState(true);
    const [showExpenseDetails, setShowExpenseDetails] = useState(true);
    const [showAuditPanel, setShowAuditPanel] = useState(true);

    const [searchPerformed, setSearchPerformed] = useState(false);
    const [printdata, setPrintdata] = useState({});

    // Helper: Formats Date object to local YYYY-MM-DD
    const formatDateLocal = (dateObj) => {
        const yyyy = dateObj.getFullYear();
        const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
        const dd = String(dateObj.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    useEffect(() => {
        setSelect_company_uid(Cookies.get("select_company_uid"));
        setGuid(Cookies.get("guid")); 

        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = today.getMonth();

        const fyStartYear = mm < 3 ? yyyy - 1 : yyyy;
        const fyStart = new Date(fyStartYear, 3, 1);

        setFrom_date(formatDateLocal(fyStart));
        setTo_date(formatDateLocal(today));
    }, []);

    // Print settings
    useEffect(() => {
        if (!select_company_uid) return;

        const q = query(
            collection(db, "accountingfinancedb", select_company_uid, "print_setting")
        );

        const unsub = onSnapshot(q, (snapshot) => {
            if (snapshot.empty) return;
            const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            const settingArray = data[0]?.settingdata || [];
            const settingObj = Object.fromEntries(
                settingArray.map(item => [item.key, item.value])
            );
            setPrintdata(settingObj);
        });

        return () => unsub();
    }, [select_company_uid]);

    // Fetch account running balance as of date
    const getAccountBalanceAsOf = async (accountguid, asOfDate) => {
        if (!select_company_uid || !accountguid) return 0;

        try {
            const endTimestamp = Timestamp.fromDate(new Date(`${asOfDate}T23:59:59`));
            const ledgerCollection = collection(
                db,
                servicetablename,
                select_company_uid,
                "ledger_books"
            );

            const q = query(
                ledgerCollection,
                where("accountguid", "==", accountguid),
                where("date_time", "<=", endTimestamp),
                orderBy("date_time", "desc"),
                limit(1)
            );

            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                return snapshot.docs[0].data().balance || 0;
            }
            return 0;
        } catch (error) {
            console.error("Error fetching balance:", error);
            return 0;
        }
    };

    /**
     * Account Classifier respecting account_role_type:
     * "income", "expense", "assets", "liabilities", "equity"
     */
    const classifyAccount = (account) => {
        const roleType = (account.account_role_type || "").toLowerCase().trim();
        const accType = (account.accounttype || account.type || "").toLowerCase().trim();
        const accName = (account.name || account.accountname || "").toLowerCase().trim();

        // 1. Primary Classification by account_role_type
        if (roleType === 'income' || roleType === 'revenue') return 'income';
        if (roleType === 'expense' || roleType === 'expenses') return 'expense';
        if (roleType === 'liabilities' || roleType === 'liability') return 'liabilities';
        if (roleType === 'equity' || roleType === 'capital') return 'equity';

        if (roleType === 'assets' || roleType === 'asset') {
            if (accName.includes("receivable") || accName.includes("debtor") || accName.includes("sundry debtor") ||
                accType.includes("receivable") || accType.includes("debtor")) {
                return 'receivables';
            }
            if (accName.includes("inventory") || accName.includes("stock") || accName.includes("stock in hand") ||
                accType.includes("inventory") || accType.includes("stock")) {
                return 'inventory';
            }
            return 'other_assets';
        }

        // 2. Fallback Classification by accounttype / name
        if (['income', 'revenue', 'direct income', 'indirect income', 'sales', 'sales account'].includes(accType) ||
            accName.includes("sales") || accName.includes("revenue")) {
            return 'income';
        }

        if (['expense', 'expenses', 'direct expenses', 'indirect expenses', 'purchase', 'purchase account', 'selling & distribution expense'].includes(accType) ||
            accName.includes("expense") || accName.includes("purchase")) {
            return 'expense';
        }

        if (['liability', 'payable', 'creditor', 'creditors', 'current liabilities', 'loans liabilities', 'loans', 'duties & taxes', 'duties and taxes'].includes(accType) ||
            accName.includes("payable") || accName.includes("creditor")) {
            return 'liabilities';
        }

        if (['equity', 'capital', 'retained_earnings', 'capital account liabilities', 'capital account', 'reserves & surplus', 'reserves'].includes(accType) ||
            accName.includes("capital") || accName.includes("equity")) {
            return 'equity';
        }

        if (accName.includes("receivable") || accName.includes("debtor") || accName.includes("sundry debtor") ||
            accType.includes("receivable") || accType.includes("debtor")) {
            return 'receivables';
        }

        if (accName.includes("inventory") || accName.includes("stock") || accName.includes("stock in hand") ||
            accType.includes("inventory") || accType.includes("stock")) {
            return 'inventory';
        }

        if (['asset', 'cash', 'bank', 'fixed assets', 'current assets', 'investments assets', 'investments'].includes(accType) ||
            accName.includes("cash") || accName.includes("bank")) {
            return 'other_assets';
        }

        return 'unclassified';
    };

    // Fetch POS Receipt Summary between dates
    const fetchVoucherSummaryDataRange = async (startDateStr, endDateStr) => {
        if (!select_company_uid || !startDateStr || !endDateStr) return { totalCreditAmount: 0, summaryList: [] };

        try {
            const startTimestamp = Timestamp.fromDate(new Date(`${startDateStr}T00:00:00`));
            const endTimestamp = Timestamp.fromDate(new Date(`${endDateStr}T23:59:59`));

            const baseRef = collection(db, servicetablename, select_company_uid, "voucher_print");
            const q = query(
                baseRef,
                where("receiptstatus", "==", true),
                where("date_time", ">=", startTimestamp),
                where("date_time", "<=", endTimestamp)
            );

            const snapshot = await getDocs(q);
            let total_credit_amount = 0;
            const voucherMap = new Map();

            snapshot.forEach((doc) => {
                const data = doc.data();
                const voucherName = data.voucher || "General Receipt";
                const amount = Number(data.totalamount || 0);
                const voucherType = data.voucher_type || "receipt";

                total_credit_amount += amount;

                if (voucherMap.has(voucherName)) {
                    const existing = voucherMap.get(voucherName);
                    existing.total_amount += amount;
                    existing.count += 1;
                    if (data.voucherid) existing.voucherids.add(data.voucherid);
                } else {
                    voucherMap.set(voucherName, {
                        voucher: voucherName,
                        voucher_type: voucherType,
                        total_amount: amount,
                        count: 1,
                        voucherids: new Set(data.voucherid ? [data.voucherid] : [])
                    });
                }
            });

            const summaryList = Array.from(voucherMap.values()).map(voucher => ({
                ...voucher,
                voucherids: Array.from(voucher.voucherids)
            })).sort((a, b) => b.total_amount - a.total_amount);

            return { totalCreditAmount: total_credit_amount, summaryList };
        } catch (error) {
            console.error("Voucher summary error:", error);
            return { totalCreditAmount: 0, summaryList: [] };
        }
    };

    // Generate Monthly Comparative Breakdown
    const generateMonthlyBreakdownData = async (allAccounts) => {
        const months = [];
        let curr = new Date(`${from_date}T00:00:00`);
        const endLimit = new Date(`${to_date}T23:59:59`);

        while (curr <= endLimit) {
            const year = curr.getFullYear();
            const month = curr.getMonth();
            
            const monthStart = new Date(year, month, 1);
            const monthEnd = new Date(year, month + 1, 0);
            const effectiveEnd = monthEnd > endLimit ? endLimit : monthEnd;
            
            const monthEndStr = formatDateLocal(effectiveEnd);
            const monthStartStr = formatDateLocal(monthStart);
            const prevMonthEndStr = formatDateLocal(new Date(monthStart.getTime() - 86400000));
            
            const monthLabel = curr.toLocaleString('default', { month: 'short', year: 'numeric' });

            let mAssets = 0, mLiabilities = 0, mEquity = 0;
            let mIncome = 0, mExpense = 0;
            let mCumIncome = 0, mCumExpense = 0;

            for (const docSnapshot of allAccounts) {
                const account = docSnapshot.data();
                const endBal = await getAccountBalanceAsOf(account.accountguid, monthEndStr);
                const startBal = await getAccountBalanceAsOf(account.accountguid, prevMonthEndStr);
                const periodDiff = endBal - startBal;
                
                const absEnd = Math.abs(endBal);
                const absDiff = Math.abs(periodDiff);

                const classification = classifyAccount(account);
                if (['receivables', 'inventory', 'other_assets'].includes(classification)) {
                    mAssets += absEnd;
                } else if (classification === 'liabilities') {
                    mLiabilities += absEnd;
                } else if (classification === 'equity') {
                    mEquity += absEnd;
                } else if (classification === 'income') {
                    mIncome += absDiff;
                    mCumIncome += absEnd;
                } else if (classification === 'expense') {
                    mExpense += absDiff;
                    mCumExpense += absEnd;
                }
            }

            if (includePosReceipts) {
                const { totalCreditAmount } = await fetchVoucherSummaryDataRange(monthStartStr, monthEndStr);
                mIncome += totalCreditAmount;
                mCumIncome += totalCreditAmount;
            }

            const mNetProfit = mIncome - mExpense;
            const mCumNetProfit = mCumIncome - mCumExpense;
            const mTotalLiabEquity = mLiabilities + mEquity + mCumNetProfit;
            const mIsBalanced = Math.abs(mAssets - mTotalLiabEquity) < 1;

            months.push({
                monthLabel,
                monthStartStr,
                monthEndStr,
                totalAssets: mAssets,
                totalLiabilities: mLiabilities,
                totalEquity: mEquity,
                totalIncome: mIncome,
                totalExpense: mExpense,
                netProfit: mNetProfit,
                cumNetProfit: mCumNetProfit,
                totalLiabEquity: mTotalLiabEquity,
                isBalanced: mIsBalanced,
                variance: Math.abs(mAssets - mTotalLiabEquity)
            });

            curr = new Date(year, month + 1, 1);
        }

        return months;
    };

    // Main Balance Sheet Generator
    const generateStatement = async () => {
        if (!select_company_uid || !from_date || !to_date) {
            Toastshow("Please select both From and To dates", "warning");
            return;
        }

        const fromTs = new Date(`${from_date}T00:00:00`).getTime();
        const toTs = new Date(`${to_date}T00:00:00`).getTime();

        if (fromTs > toTs) {
            Toastshow("From Date cannot be after To Date", "warning");
            return;
        }

        setIsLoading(true);
        setSearchPerformed(true);

        try {
            const fromD = new Date(`${from_date}T00:00:00`);
            const prevD = new Date(fromD.getTime() - 86400000);
            const prevDateStr = formatDateLocal(prevD);

            // 1. Fetch POS Receipt Summary Total
            const { totalCreditAmount, summaryList } = await fetchVoucherSummaryDataRange(from_date, to_date);
            setVoucherSummary(summaryList);
            setTotalCredit(totalCreditAmount);

            // 2. Fetch Master Ledger Accounts
            const accountsSnapshot = await getDocs(
                query(
                    collection(db, servicetablename, select_company_uid, "ledger_accounts"),
                    where("usecase", "==", "master")
                )
            );

            const receivablesList = [];
            const inventoryList = [];
            const otherAssetsList = [];
            const liabilitiesList = [];
            const equityList = [];
            const incomeList = [];
            const expenseList = [];
            const unclassifiedList = [];

            let recSum = 0;
            let invSum = 0;
            let othAssetSum = 0;
            let liabilitiesSum = 0;
            let equitySum = 0;

            let periodIncomeSum = 0;
            let periodExpenseSum = 0;
            let cumIncomeSum = 0;
            let cumExpenseSum = 0;

            const asOnDate = to_date;

            for (const docSnapshot of accountsSnapshot.docs) {
                const account = docSnapshot.data();
                const cumBalance = await getAccountBalanceAsOf(account.accountguid, asOnDate);
                const startBalance = await getAccountBalanceAsOf(account.accountguid, prevDateStr);
                const periodDiff = cumBalance - startBalance;
                
                const absCum = Math.abs(cumBalance);
                const absPeriod = Math.abs(periodDiff);

                if (cumBalance !== 0 || periodDiff !== 0) {
                    const accountData = {
                        name: account.name || account.accountname || "Unnamed Account",
                        accountnumber: account.accountnumber || "-",
                        accountguid: account.accountguid,
                        roleType: account.account_role_type || "None",
                        accountType: account.accounttype || account.type || "None",
                        balance: absPeriod > 0 ? absPeriod : absCum,
                        cumBalance: absCum
                    };

                    const classification = classifyAccount(account);

                    if (classification === 'receivables') {
                        if (absCum > 0) receivablesList.push(accountData);
                        recSum += absCum;
                    } else if (classification === 'inventory') {
                        if (absCum > 0) inventoryList.push(accountData);
                        invSum += absCum;
                    } else if (classification === 'other_assets') {
                        if (absCum > 0) otherAssetsList.push(accountData);
                        othAssetSum += absCum;
                    } else if (classification === 'liabilities') {
                        if (absCum > 0) liabilitiesList.push(accountData);
                        liabilitiesSum += absCum;
                    } else if (classification === 'equity') {
                        if (absCum > 0) equityList.push(accountData);
                        equitySum += absCum;
                    } else if (classification === 'income') {
                        if (absPeriod > 0) incomeList.push(accountData);
                        periodIncomeSum += absPeriod;
                        cumIncomeSum += absCum;
                    } else if (classification === 'expense') {
                        if (absPeriod > 0) expenseList.push(accountData);
                        periodExpenseSum += absPeriod;
                        cumExpenseSum += absCum;
                    } else if (classification === 'unclassified') {
                        unclassifiedList.push(accountData);
                    }
                }
            }

            // 3. Add POS Receipt Summary if checked
            if (includePosReceipts && totalCreditAmount > 0) {
                const receiptIncomeEntry = {
                    name: `POS Receipts Revenue (${from_date} to ${to_date})`,
                    accountnumber: "POS-REC",
                    accountguid: "pos_receipt_income_summary",
                    balance: totalCreditAmount
                };
                incomeList.unshift(receiptIncomeEntry);
                periodIncomeSum += totalCreditAmount;
                cumIncomeSum += totalCreditAmount;
            }

            const periodNetProfitVal = periodIncomeSum - periodExpenseSum;
            const cumNetProfitVal = cumIncomeSum - cumExpenseSum;
            const priorNetProfitVal = cumNetProfitVal - periodNetProfitVal;

            receivablesList.sort((a, b) => b.balance - a.balance);
            inventoryList.sort((a, b) => b.balance - a.balance);
            otherAssetsList.sort((a, b) => b.balance - a.balance);
            liabilitiesList.sort((a, b) => b.balance - a.balance);
            equityList.sort((a, b) => b.balance - a.balance);
            incomeList.sort((a, b) => b.balance - a.balance);
            expenseList.sort((a, b) => b.balance - a.balance);

            setReceivableAssets(receivablesList);
            setInventoryAssets(inventoryList);
            setOtherAssets(otherAssetsList);
            setLiabilities(liabilitiesList);
            setEquity(equityList);
            setIncomeAccounts(incomeList);
            setExpenseAccounts(expenseList);
            setUnclassifiedAccounts(unclassifiedList);

            setTotalReceivables(recSum);
            setTotalInventory(invSum);
            setTotalOtherAssets(othAssetSum);
            setTotalAssets(recSum + invSum + othAssetSum);

            setTotalLiabilities(liabilitiesSum);
            setTotalEquityAccounts(equitySum);
            setTotalIncome(periodIncomeSum);
            setTotalExpense(periodExpenseSum);
            setNetProfit(periodNetProfitVal);
            setPriorNetProfit(priorNetProfitVal);
            setCumNetProfit(cumNetProfitVal);

            // 4. Generate Monthly Breakdown
            const monthsData = await generateMonthlyBreakdownData(accountsSnapshot.docs);
            setMonthlyBreakdown(monthsData);

            Toastshow("Statement generated successfully", "success");
        } catch (error) {
            console.error("Statement generation error:", error);
            Toastshow("Error generating statement", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return `₹${Number(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
    };

    const grandTotalVouchers = voucherSummary.reduce((sum, v) => sum + v.total_amount, 0);
    const totalTransactions = voucherSummary.reduce((sum, v) => sum + v.count, 0);
    const totalUniqueIDs = voucherSummary.reduce((sum, v) => sum + v.voucherids.length, 0);

    const finalEquityTotal = totalEquityAccounts + cumNetProfit;
    const finalLiabilitiesAndEquity = totalLiabilities + finalEquityTotal;
    const collationDifference = totalAssets - finalLiabilitiesAndEquity;
    const isBalanced = Math.abs(collationDifference) < 1;

    // PDF Download with Itemized Incomes & Expenses List
    const downloadPDF = () => {
        if (!searchPerformed) {
            Toastshow("No data to generate report", "warning");
            return;
        }

        const renderAssetRows = (title, list) => {
            if (!list || list.length === 0) return '';
            return `
                <tr class="sub-header"><td colspan="2">${title}</td></tr>
                ${list.map(acc => `
                    <tr>
                        <td class="text-left">${acc.name}</td>
                        <td class="text-right">${formatCurrency(acc.balance)}</td>
                    </tr>
                `).join('')}
            `;
        };

        const singleViewHTML = `
            <div class="bs-wrapper">
                <!-- Assets -->
                <div class="bs-col">
                    <h3>ASSETS</h3>
                    <table>
                        <thead>
                            <tr>
                                <th class="text-left">Particulars</th>
                                <th class="text-right">Amount (₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${renderAssetRows("Accounts Receivable (Debtors)", receivableAssets)}
                            ${renderAssetRows("Inventory / Stock", inventoryAssets)}
                            ${renderAssetRows("Other Cash & Assets", otherAssets)}
                            ${totalAssets === 0 ? '<tr><td colspan="2" class="no-data">No asset balances found</td></tr>' : ''}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td class="text-left bold">TOTAL ASSETS</td>
                                <td class="text-right bold">${formatCurrency(totalAssets)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <!-- Liabilities & Equity -->
                <div class="bs-col">
                    <h3 class="liability">LIABILITIES & EQUITY</h3>
                    <table>
                        <thead>
                            <tr>
                                <th class="text-left">Particulars</th>
                                <th class="text-right">Amount (₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="sub-header"><td colspan="2">Liabilities</td></tr>
                            ${liabilities.length > 0 ? liabilities.map(acc => `
                                <tr>
                                    <td class="text-left">${acc.name}</td>
                                    <td class="text-right">${formatCurrency(acc.balance)}</td>
                                </tr>
                            `).join('') : '<tr><td colspan="2" class="no-data">No liabilities found</td></tr>'}
                            
                            <tr class="sub-header"><td colspan="2">Capital & Equity</td></tr>
                            ${equity.length > 0 ? equity.map(acc => `
                                <tr>
                                    <td class="text-left">${acc.name}</td>
                                    <td class="text-right">${formatCurrency(acc.balance)}</td>
                                </tr>
                            `).join('') : ''}
                            
                            <tr>
                                <td class="text-left bold">Prior Retained Earnings</td>
                                <td class="text-right bold">${formatCurrency(priorNetProfit)}</td>
                            </tr>
                            <tr class="profit-loss-row">
                                <td class="text-left bold">Current Period Net Profit / (Loss)</td>
                                <td class="text-right bold ${netProfit < 0 ? 'text-danger' : ''}">${formatCurrency(netProfit)}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td class="text-left bold">TOTAL LIABILITIES & EQUITY</td>
                                <td class="text-right bold">${formatCurrency(finalLiabilitiesAndEquity)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        `;

        const monthlyViewHTML = `
            <div style="margin-top:15px;">
                <h3 style="font-size:11pt; font-weight:bold; margin-bottom:8px; text-transform:uppercase;">Monthly Balance Sheet Comparison (${from_date} to ${to_date})</h3>
                <table class="report-table">
                    <thead>
                        <tr>
                            <th class="text-left">Month</th>
                            <th class="text-right">Assets (₹)</th>
                            <th class="text-right">Liabilities (₹)</th>
                            <th class="text-right">Equity (₹)</th>
                            <th class="text-right">Incomes (₹)</th>
                            <th class="text-right">Expenses (₹)</th>
                            <th class="text-right">Net Profit (₹)</th>
                            <th class="text-center">Collation</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${monthlyBreakdown.map(m => `
                            <tr>
                                <td><b>${m.monthLabel}</b></td>
                                <td class="text-right">${formatCurrency(m.totalAssets)}</td>
                                <td class="text-right">${formatCurrency(m.totalLiabilities)}</td>
                                <td class="text-right">${formatCurrency(m.totalEquity)}</td>
                                <td class="text-right" style="color:#15803d;">${formatCurrency(m.totalIncome)}</td>
                                <td class="text-right" style="color:#dc2626;">${formatCurrency(m.totalExpense)}</td>
                                <td class="text-right bold">${formatCurrency(m.netProfit)}</td>
                                <td class="text-center bold" style="color:${m.isBalanced ? '#15803d' : '#b91c1c'}">
                                    ${m.isBalanced ? '✓ Matched' : '⚠ Unmatched'}
                                </td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        `;

        // Itemized Incomes & Expenses List Tables for PDF
        const incomesPDFList = `
            <div style="margin-top:20px; page-break-inside: avoid;">
                <h3 style="font-size:11pt; font-weight:bold; color:#15803d; margin-bottom:6px; text-transform:uppercase;">Itemized Incomes Breakdown (${from_date} to ${to_date})</h3>
                <table class="report-table">
                    <thead>
                        <tr style="background:#f0fdf4;">
                            <th class="text-left">#</th>
                            <th class="text-left">Income Source / Category</th>
                            <th class="text-left">Account Number</th>
                            <th class="text-right">Amount (₹)</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${incomeAccounts.map((inc, idx) => `
                            <tr>
                                <td style="width:30px;">${idx + 1}</td>
                                <td><b>${inc.name}</b></td>
                                <td>${inc.accountnumber || '-'}</td>
                                <td class="text-right bold" style="color:#15803d;">${formatCurrency(inc.balance)}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colspan="3" class="bold">TOTAL PERIOD INCOMES</td>
                            <td class="text-right bold" style="color:#15803d;">${formatCurrency(totalIncome)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        `;

        const expensesPDFList = `
            <div style="margin-top:20px; page-break-inside: avoid;">
                <h3 style="font-size:11pt; font-weight:bold; color:#dc2626; margin-bottom:6px; text-transform:uppercase;">Itemized Expenses Breakdown (${from_date} to ${to_date})</h3>
                <table class="report-table">
                    <thead>
                        <tr style="background:#fef2f2;">
                            <th class="text-left">#</th>
                            <th class="text-left">Expense Account / Category</th>
                            <th class="text-left">Account Number</th>
                            <th class="text-right">Amount (₹)</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${expenseAccounts.map((exp, idx) => `
                            <tr>
                                <td style="width:30px;">${idx + 1}</td>
                                <td><b>${exp.name}</b></td>
                                <td>${exp.accountnumber || '-'}</td>
                                <td class="text-right bold" style="color:#dc2626;">${formatCurrency(exp.balance)}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colspan="3" class="bold">TOTAL PERIOD EXPENSES</td>
                            <td class="text-right bold" style="color:#dc2626;">${formatCurrency(totalExpense)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        `;

        const content = `
            <div class="a4-container">
                <table class="b-bottom">
                    <tr>
                        <td>
                            <div class="title_sub">${printdata.print_title || "COMPANY NAME"}</div>
                            <div class="text-h2">${printdata.print_tagline || ""}</div>
                            <div class="text-h3">${printdata.print_contactinfo || ""}</div>
                            <div class="text-h3 bold">${printdata.print_address || ""}</div>
                        </td>
                        <td style="text-align:right">
                            <div class="text-h2 bold" style="font-size:15pt; color:#0f172a;">${viewMode === 'monthly' ? 'MONTHLY BALANCE SHEET' : 'BALANCE SHEET'}</div>
                            <div class="text-h3">Period: ${from_date} to ${to_date}</div>
                            <div class="text-h3 bold p-top">Total Assets: ${formatCurrency(totalAssets)}</div>
                            <div class="text-h3 bold" style="color:#15803d;">Period Incomes: ${formatCurrency(totalIncome)}</div>
                            <div class="text-h3 bold" style="color:#dc2626;">Period Expenses: ${formatCurrency(totalExpense)}</div>
                            <div class="text-h3 bold">Period Net Profit: ${formatCurrency(netProfit)}</div>
                        </td>
                    </tr>
                </table>

                ${viewMode === 'monthly' ? monthlyViewHTML : singleViewHTML}
                ${incomesPDFList}
                ${expensesPDFList}

                <div class="footer">
                    <p style="font-weight:bold; color:${isBalanced ? '#15803d' : '#b91c1c'}">
                        ${isBalanced ? "✓ Balance Sheet Balanced & Collation Matched" : `⚠ Collation Not Matched (Variance: ${formatCurrency(Math.abs(collationDifference))})`}
                    </p>
                    <p>Generated by: ${printdata.print_footer || "Aooz.in"} | Date Range: ${from_date} to ${to_date}</p>
                </div>
            </div>
        `;

        const fileName = `Balance_Sheet_${viewMode}_${from_date}_to_${to_date}.pdf`;
        const printWindow = window.open("", "", "width=1200,height=800");
        printWindow.document.write(`
            <html>
            <head>
                <title>Balance Sheet & Itemized Statement (${from_date} to ${to_date})</title>
                <style>
                    @page { size: A4 portrait; margin: 8mm; }
                    body { font-family: Arial, sans-serif; margin: 0; padding: 0; color: #1e293b; font-size: 9.5pt; }
                    .a4-container { width: 190mm; margin: 0 auto; box-sizing: border-box; }
                    .b-bottom { width: 100%; border-bottom: 2px solid #0f172a; padding-bottom: 8px; margin-bottom: 12px; }
                    .title_sub { font-size: 16pt; font-weight: bold; text-transform: uppercase; color: #0f172a; }
                    .text-h2 { font-size: 10pt; margin: 2px 0; }
                    .text-h3 { font-size: 9pt; margin: 2px 0; }
                    .bold { font-weight: bold; }
                    .p-top { margin-top: 6px; }
                    .bs-wrapper { display: flex; gap: 6mm; margin-top: 8px; }
                    .bs-col { flex: 1; border: 1px solid #cbd5e1; border-radius: 4px; box-sizing: border-box; }
                    .bs-col h3 { text-align: center; background: #1e293b; color: white; padding: 5px; margin: 0; font-size: 10.5pt; letter-spacing: 0.5px; }
                    .bs-col h3.liability { background: #475569; }
                    table { width: 100%; border-collapse: collapse; }
                    th, td { padding: 5px 6px; border-bottom: 1px solid #e2e8f0; font-size: 8.5pt; }
                    th { background: #f8fafc; font-weight: bold; color: #475569; border-bottom: 2px solid #cbd5e1; }
                    .text-left { text-align: left; }
                    .text-right { text-align: right; }
                    .text-center { text-align: center; }
                    .sub-header td { background: #f1f5f9; font-weight: bold; padding: 5px; text-transform: uppercase; font-size: 8pt; color: #334155; }
                    .profit-loss-row td { background: #fefce8; border-top: 1px solid #fef08a; border-bottom: 1px solid #fef08a; }
                    tfoot td { background: #e2e8f0; font-weight: bold; border-top: 2px solid #94a3b8; font-size: 9pt; }
                    .report-table th, .report-table td { border: 1px solid #cbd5e1; padding: 6px; }
                    .footer { margin-top: 15px; text-align: center; font-size: 8.5pt; color: #64748b; border-top: 1px solid #cbd5e1; padding-top: 8px; }
                    .no-data { text-align: center; padding: 10px; color: #94a3b8; font-style: italic; }
                    @media print {
                        body { margin: 0; }
                        .a4-container { width: 190mm; margin: 0; }
                        tr, td, th { page-break-inside: avoid; }
                    }
                </style>
            </head>
            <body>${content}</body>
            </html>
        `);

        printWindow.document.close();
        printWindow.document.title = fileName;
        printWindow.print();
    };

    return (
        <div className="balance-sheet-container">
            <style>{`
                .view-toggle-bar {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 14px;
                    background: #f1f5f9;
                    padding: 4px;
                    border-radius: 8px;
                    width: fit-content;
                }
                .view-toggle-btn {
                    padding: 6px 14px;
                    border-radius: 6px;
                    font-size: 13px;
                    font-weight: 600;
                    border: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    transition: all 0.2s ease;
                }
                .view-toggle-btn.active {
                    background: #0f172a;
                    color: #ffffff;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }
                .view-toggle-btn.inactive {
                    background: transparent;
                    color: #475569;
                }

                .bs-card-summary {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                    gap: 12px;
                    margin-bottom: 16px;
                }
                .bs-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 12px 14px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                }
                .bs-card-title {
                    font-size: 11px;
                    font-weight: 600;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }
                .bs-card-value {
                    font-size: 16px;
                    font-weight: 700;
                    color: #0f172a;
                    margin-top: 4px;
                }
                .bs-card-value.primary { color: #2563eb; }
                .bs-card-value.amber { color: #d97706; }
                .bs-card-value.success { color: #16a34a; }
                .bs-card-value.danger { color: #dc2626; }
                
                .professional-bs {
                    font-family: 'Segoe UI', Roboto, sans-serif;
                    color: #1e293b;
                    padding: 20px;
                    background: #fff;
                    border-radius: 8px;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
                    margin-top: 15px;
                }
                .professional-bs .bs-grid {
                    display: flex;
                    gap: 20px;
                }
                .professional-bs .bs-col {
                    flex: 1;
                    border: 1px solid #cbd5e1;
                    border-radius: 8px;
                    overflow: hidden;
                    background: #ffffff;
                }
                .professional-bs .bs-col-header {
                    background: #0f172a;
                    color: #fff;
                    text-align: center;
                    padding: 12px;
                    font-size: 14px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }
                .professional-bs .bs-col-header.liability {
                    background: #334155;
                }
                .professional-bs table {
                    width: 100%;
                    border-collapse: collapse;
                }
                .professional-bs th {
                    background: #f8fafc;
                    padding: 10px 12px;
                    text-align: left;
                    font-size: 12px;
                    border-bottom: 2px solid #e2e8f0;
                    color: #475569;
                    font-weight: 600;
                }
                .professional-bs td {
                    padding: 9px 12px;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 13px;
                }
                .professional-bs .text-right { text-align: right; }
                .professional-bs .bold { font-weight: 600; }
                .professional-bs .sub-header td {
                    background: #f1f5f9;
                    font-weight: 700;
                    color: #1e293b;
                    padding: 8px 12px;
                    border-bottom: 1px solid #cbd5e1;
                    text-transform: uppercase;
                    font-size: 11px;
                    letter-spacing: 0.5px;
                }
                .professional-bs .profit-row td {
                    background: #fefce8;
                    border-top: 1px solid #fef08a;
                    border-bottom: 1px solid #fef08a;
                }
                .professional-bs tfoot td {
                    background: #e2e8f0;
                    font-weight: 700;
                    border-top: 2px solid #94a3b8;
                    padding: 12px;
                    font-size: 14px;
                }

                .collation-banner {
                    margin-top: 15px;
                    padding: 12px 16px;
                    border-radius: 8px;
                    font-weight: 600;
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .collation-banner.balanced {
                    background: #f0fdf4;
                    color: #15803d;
                    border: 1px solid #bbf7d0;
                }
                .collation-banner.unbalanced {
                    background: #fef2f2;
                    color: #b91c1c;
                    border: 1px solid #fecaca;
                }

                .audit-panel {
                    margin-top: 15px;
                    background: #ffffff;
                    border: 1px solid #fecaca;
                    border-radius: 8px;
                    padding: 16px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                }

                .income-section, .expense-section, .pos-section, .monthly-section {
                    margin-top: 20px;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
                    overflow: hidden;
                }

                .income-header {
                    background: #f0fdf4;
                    padding: 12px 18px;
                    border-bottom: 1px solid #bbf7d0;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    cursor: pointer;
                }
                .expense-header {
                    background: #fef2f2;
                    padding: 12px 18px;
                    border-bottom: 1px solid #fecaca;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    cursor: pointer;
                }
                .pos-header {
                    background: #f8fafc;
                    padding: 12px 18px;
                    border-bottom: 1px solid #e2e8f0;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    cursor: pointer;
                }
            `}</style>

            {/* Header Navigation */}
            <div className="b-bottom">
                <table className="w-100">
                    <tbody>
                        <tr>
                            <td>
                                <div
                                    onClick={() => navigate("/?page=reports")}
                                    className="button-ui0 text-h2 bold p-2 cursor fcap"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                >
                                    <MdArrowBackIos className="icon" /> Balance Sheet & Statement ({from_date} to {to_date})
                                </div>
                            </td>
                            <td width={30}>
                                <div onClick={() => navigate(-1)} className="button-ui0 p-2 cursor">
                                    <MdClose className="icon" />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Date Range Selection & View Mode Selector */}
            <div className="container_size_info p-2">
                
                {/* View Mode Tabs */}
                <div className="view-toggle-bar">
                    <button 
                        className={`view-toggle-btn ${viewMode === 'single' ? 'active' : 'inactive'}`}
                        onClick={() => setViewMode('single')}
                    >
                        <MdViewWeek size={18} /> Single Period Statement
                    </button>
                    <button 
                        className={`view-toggle-btn ${viewMode === 'monthly' ? 'active' : 'inactive'}`}
                        onClick={() => setViewMode('monthly')}
                    >
                        <MdCalendarMonth size={18} /> Monthly Comparative Balance Sheets
                    </button>
                </div>

                <div className="controls-panel mb-3">
                    <table>
                        <tbody>
                            <tr>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                        <TbReport className="icon" style={{ fontSize: '20px' }} />
                                        <div>
                                            <label className="mr-1">From Date:</label>
                                            <input
                                                type="date"
                                                value={from_date}
                                                onChange={(e) => setFrom_date(e.target.value)}
                                                className="date-input"
                                            />
                                        </div>
                                        <div>
                                            <label className="mr-1">To Date:</label>
                                            <input
                                                type="date"
                                                value={to_date}
                                                onChange={(e) => setTo_date(e.target.value)}
                                                className="date-input"
                                            />
                                        </div>

                                        {/* POS Receipt Overlay Toggle */}
                                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer', background: '#f8fafc', padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
                                            <input 
                                                type="checkbox"
                                                checked={includePosReceipts}
                                                onChange={(e) => setIncludePosReceipts(e.target.checked)}
                                            />
                                            Include POS Receipts Summary in Income
                                        </label>

                                        <button
                                            onClick={generateStatement}
                                            className="button-ui cursor p-1"
                                            disabled={isLoading}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                        >
                                            <MdSearch className="icon" />
                                            {isLoading ? "Generating..." : "Generate Statement"}
                                        </button>
                                    </div>
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    <button
                                        className="button-ui2 p-1 cursor"
                                        onClick={downloadPDF}
                                        disabled={!searchPerformed}
                                        style={{ 
                                            opacity: searchPerformed ? 1 : 0.5,
                                            display: 'inline-flex', alignItems: 'center', gap: '4px' 
                                        }}
                                    >
                                        <MdFileDownload className="icon" /> Download PDF Statement
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Loading State */}
                {isLoading && (
                    <div className="text-center p-3">
                        <div className="loader">Processing Balance Sheet & Monthly Financial Accounts...</div>
                    </div>
                )}

                {/* Main Content Display */}
                {searchPerformed && !isLoading && (
                    <>
                        {/* Summary KPI Cards */}
                        <div className="bs-card-summary">
                            <div className="bs-card">
                                <div className="bs-card-title">Total Assets</div>
                                <div className="bs-card-value">{formatCurrency(totalAssets)}</div>
                            </div>
                            <div className="bs-card">
                                <div className="bs-card-title">
                                    <MdTrendingUp style={{ color: '#16a34a' }} /> Period Incomes
                                </div>
                                <div className="bs-card-value success">{formatCurrency(totalIncome)}</div>
                            </div>
                            <div className="bs-card">
                                <div className="bs-card-title">
                                    <MdTrendingDown style={{ color: '#dc2626' }} /> Period Expenses
                                </div>
                                <div className="bs-card-value danger">{formatCurrency(totalExpense)}</div>
                            </div>
                            <div className="bs-card">
                                <div className="bs-card-title">
                                    <MdAccountBalanceWallet style={{ color: '#2563eb' }} /> Receivables
                                </div>
                                <div className="bs-card-value primary">{formatCurrency(totalReceivables)}</div>
                            </div>
                            <div className="bs-card">
                                <div className="bs-card-title">
                                    <MdInventory style={{ color: '#d97706' }} /> Inventory
                                </div>
                                <div className="bs-card-value amber">{formatCurrency(totalInventory)}</div>
                            </div>
                            <div className="bs-card">
                                <div className="bs-card-title">Period Net Profit</div>
                                <div className={`bs-card-value ${netProfit >= 0 ? 'success' : 'danger'}`}>
                                    {formatCurrency(netProfit)}
                                </div>
                            </div>
                            <div className="bs-card">
                                <div className="bs-card-title">Collation Variance</div>
                                <div className={`bs-card-value ${isBalanced ? 'success' : 'danger'}`}>
                                    {formatCurrency(Math.abs(collationDifference))}
                                </div>
                            </div>
                        </div>

                        {/* Collation Status Banner */}
                        <div className={`collation-banner ${isBalanced ? 'balanced' : 'unbalanced'}`}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {isBalanced ? (
                                    <>
                                        <MdCheckCircle style={{ fontSize: '20px' }} />
                                        <span><strong>Collation Matched:</strong> Total Assets ({formatCurrency(totalAssets)}) = Total Liabilities & Equity ({formatCurrency(finalLiabilitiesAndEquity)})</span>
                                    </>
                                ) : (
                                    <>
                                        <MdWarning style={{ fontSize: '20px' }} />
                                        <span><strong>Collation Not Matched:</strong> Difference of {formatCurrency(Math.abs(collationDifference))}</span>
                                    </>
                                )}
                            </div>
                            <button 
                                onClick={() => setShowAuditPanel(!showAuditPanel)}
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: '700', color: 'inherit', textDecoration: 'underline', display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                                <MdHelpOutline size={18} /> {showAuditPanel ? "Hide Fix Guide" : "How to Fix Collation?"}
                            </button>
                        </div>

                        {/* Audit & Collation Fix Panel */}
                        {showAuditPanel && (
                            <div className="audit-panel">
                                <h4 style={{ margin: '0 0 10px 0', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <MdBuild size={20} /> Collation Math Analysis & Fix Instructions
                                </h4>

                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px', marginBottom: '12px', fontSize: '13px', fontFamily: 'monospace' }}>
                                    <div><strong>• Total Assets:</strong> {formatCurrency(totalAssets)}</div>
                                    <div><strong>• Liabilities:</strong> {formatCurrency(totalLiabilities)}</div>
                                    <div><strong>• Recorded Capital/Equity:</strong> {formatCurrency(totalEquityAccounts)}</div>
                                    <div><strong>• All-time Cumulative Net Loss:</strong> {formatCurrency(cumNetProfit)}</div>
                                    <hr style={{ margin: '8px 0', border: '0', borderTop: '1px dashed #cbd5e1' }} />
                                    <div style={{ color: '#b91c1c', fontWeight: 'bold' }}>
                                        =&gt; Unreconciled Opening Equity Needed = {formatCurrency(Math.abs(collationDifference))}
                                    </div>
                                </div>

                                <div style={{ fontSize: '13px', color: '#334155', lineHeight: '1.5' }}>
                                    <strong>How to Fix this Variance ({formatCurrency(Math.abs(collationDifference))}):</strong>
                                    <ol style={{ margin: '6px 0 0 20px', padding: 0 }}>
                                        <li>
                                            <strong>Add Opening Owner Equity:</strong> In Firestore <code>ledger_accounts</code>, create or update a master ledger account named <code>"Opening Capital"</code> or <code>"Owner Equity"</code> with <code>account_role_type: "equity"</code> and set its balance to <strong>{formatCurrency(Math.abs(collationDifference))}</strong>.
                                        </li>
                                        <li>
                                            <strong>Check POS Receipts Double-Counting:</strong> If POS cash is already in your bank/cash ledger accounts, uncheck <i>"Include POS Receipts Summary in Income"</i> to avoid double-counting income.
                                        </li>
                                    </ol>
                                </div>
                            </div>
                        )}

                        {/* SINGLE PERIOD BALANCE SHEET VIEW */}
                        {viewMode === 'single' && (
                            <div className="professional-bs">
                                <div className="bs-grid">
                                    {/* ASSETS COLUMN */}
                                    <div className="bs-col">
                                        <div className="bs-col-header">Assets</div>
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Particulars</th>
                                                    <th className="text-right">Amount (₹)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr className="sub-header">
                                                    <td colSpan="2">Accounts Receivable (Debtors)</td>
                                                </tr>
                                                {receivableAssets.length > 0 ? (
                                                    receivableAssets.map((acc) => (
                                                        <tr key={acc.accountguid}>
                                                            <td>{acc.name}</td>
                                                            <td className="text-right">{formatCurrency(acc.balance)}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="2" style={{ color: '#94a3b8', fontStyle: 'italic', paddingLeft: '16px' }}>No active receivables</td></tr>
                                                )}

                                                <tr className="sub-header">
                                                    <td colSpan="2">Inventory & Stock</td>
                                                </tr>
                                                {inventoryAssets.length > 0 ? (
                                                    inventoryAssets.map((acc) => (
                                                        <tr key={acc.accountguid}>
                                                            <td>{acc.name}</td>
                                                            <td className="text-right">{formatCurrency(acc.balance)}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="2" style={{ color: '#94a3b8', fontStyle: 'italic', paddingLeft: '16px' }}>No inventory recorded</td></tr>
                                                )}

                                                <tr className="sub-header">
                                                    <td colSpan="2">Other Cash & Fixed Assets</td>
                                                </tr>
                                                {otherAssets.length > 0 ? (
                                                    otherAssets.map((acc) => (
                                                        <tr key={acc.accountguid}>
                                                            <td>{acc.name}</td>
                                                            <td className="text-right">{formatCurrency(acc.balance)}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="2" style={{ color: '#94a3b8', fontStyle: 'italic', paddingLeft: '16px' }}>No other asset balances</td></tr>
                                                )}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td className="bold">Total Assets</td>
                                                    <td className="text-right bold">{formatCurrency(totalAssets)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>

                                    {/* LIABILITIES & EQUITY COLUMN */}
                                    <div className="bs-col">
                                        <div className="bs-col-header liability">Liabilities & Equity</div>
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Particulars</th>
                                                    <th className="text-right">Amount (₹)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr className="sub-header">
                                                    <td colSpan="2">Liabilities</td>
                                                </tr>
                                                {liabilities.length > 0 ? (
                                                    liabilities.map((liab) => (
                                                        <tr key={liab.accountguid}>
                                                            <td>{liab.name}</td>
                                                            <td className="text-right">{formatCurrency(liab.balance)}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="2" style={{ color: '#94a3b8', fontStyle: 'italic', paddingLeft: '16px' }}>No liability balances found</td></tr>
                                                )}

                                                <tr className="sub-header">
                                                    <td colSpan="2">Capital & Equity</td>
                                                </tr>
                                                {equity.length > 0 && (
                                                    equity.map((eq) => (
                                                        <tr key={eq.accountguid}>
                                                            <td>{eq.name}</td>
                                                            <td className="text-right">{formatCurrency(eq.balance)}</td>
                                                        </tr>
                                                    ))
                                                )}

                                                <tr className="sub-header">
                                                    <td colSpan="2">Retained Earnings & Reserves</td>
                                                </tr>
                                                <tr>
                                                    <td>Prior Retained Earnings</td>
                                                    <td className="text-right">{formatCurrency(priorNetProfit)}</td>
                                                </tr>
                                                <tr className="profit-row">
                                                    <td className="bold">
                                                        {netProfit >= 0 ? "Current Period Net Profit" : "Current Period Net Loss"}
                                                    </td>
                                                    <td className={`text-right bold ${netProfit < 0 ? 'text-danger' : ''}`}>
                                                        {formatCurrency(netProfit)}
                                                    </td>
                                                </tr>
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td className="bold">Total Liabilities & Equity</td>
                                                    <td className="text-right bold">{formatCurrency(finalLiabilitiesAndEquity)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* MONTHLY COMPARATIVE BALANCE SHEETS VIEW */}
                        {viewMode === 'monthly' && (
                            <div className="monthly-section" style={{ padding: '16px' }}>
                                <h3 style={{ margin: '0 0 14px 0', fontSize: '16px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <MdCalendarMonth style={{ color: '#2563eb' }} /> Monthly Comparative Balance Sheets ({from_date} to {to_date})
                                </h3>

                                {monthlyBreakdown.length > 0 ? (
                                    <table className="w-100" style={{ borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                                                <th className="p-2 text-left">Month</th>
                                                <th className="p-2 text-right">Assets (₹)</th>
                                                <th className="p-2 text-right">Liabilities (₹)</th>
                                                <th className="p-2 text-right">Equity (₹)</th>
                                                <th className="p-2 text-right">Incomes (₹)</th>
                                                <th className="p-2 text-right">Expenses (₹)</th>
                                                <th className="p-2 text-right">Net Profit (₹)</th>
                                                <th className="p-2 text-center">Collation Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {monthlyBreakdown.map((m) => (
                                                <tr key={m.monthLabel} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <td className="p-2"><strong>{m.monthLabel}</strong></td>
                                                    <td className="p-2 text-right bold">{formatCurrency(m.totalAssets)}</td>
                                                    <td className="p-2 text-right">{formatCurrency(m.totalLiabilities)}</td>
                                                    <td className="p-2 text-right">{formatCurrency(m.totalEquity)}</td>
                                                    <td className="p-2 text-right bold" style={{ color: '#16a34a' }}>{formatCurrency(m.totalIncome)}</td>
                                                    <td className="p-2 text-right bold" style={{ color: '#dc2626' }}>{formatCurrency(m.totalExpense)}</td>
                                                    <td className={`p-2 text-right bold ${m.netProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                                                        {formatCurrency(m.netProfit)}
                                                    </td>
                                                    <td className="p-2 text-center">
                                                        <span style={{ 
                                                            padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700',
                                                            background: m.isBalanced ? '#dcfce7' : '#fee2e2',
                                                            color: m.isBalanced ? '#15803d' : '#991b1b'
                                                        }}>
                                                            {m.isBalanced ? "✓ Matched" : `⚠ Unmatched (${formatCurrency(m.variance)})`}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="text-center p-3 text-muted">
                                        No monthly records found for the selected date range.
                                    </div>
                                )}
                            </div>
                        )}

                        {/* INCOMES BREAKDOWN SECTION */}
                        <div className="income-section">
                            <div className="income-header" onClick={() => setShowIncomeDetails(!showIncomeDetails)}>
                                <div style={{ fontSize: '15px', fontWeight: '700', color: '#166534', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <MdTrendingUp style={{ fontSize: '20px' }} />
                                    Incomes Breakdown ({from_date} to {to_date})
                                    <span style={{ fontSize: '13px', background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>
                                        Total Income: {formatCurrency(totalIncome)}
                                    </span>
                                </div>
                                <div style={{ color: '#166534' }}>
                                    {showIncomeDetails ? <MdExpandLess size={24} /> : <MdExpandMore size={24} />}
                                </div>
                            </div>

                            {showIncomeDetails && (
                                <div style={{ padding: '16px' }}>
                                    {incomeAccounts.length > 0 ? (
                                        <table className="w-100">
                                            <thead>
                                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                                    <th className="p-2 text-left">Income / Revenue Source</th>
                                                    <th className="p-2 text-right">Amount (₹)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {incomeAccounts.map((inc) => (
                                                    <tr key={inc.accountguid} style={{ borderBottom: '1px solid #f1f5f9', background: inc.accountnumber === 'POS-REC' ? '#f0fdf4' : 'transparent' }}>
                                                        <td className="p-2">
                                                            <strong>{inc.name}</strong>
                                                            {inc.accountnumber === 'POS-REC' && (
                                                                <span style={{ fontSize: '11px', background: '#bbf7d0', color: '#15803d', marginLeft: '8px', padding: '2px 6px', borderRadius: '4px' }}>
                                                                    Receipt Summary
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="p-2 text-right bold" style={{ color: '#16a34a' }}>
                                                            {formatCurrency(inc.balance)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr style={{ background: '#f1f5f9', borderTop: '2px solid #cbd5e1' }}>
                                                    <td className="p-2 bold">Total Period Incomes</td>
                                                    <td className="p-2 text-right bold" style={{ color: '#16a34a' }}>
                                                        {formatCurrency(totalIncome)}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    ) : (
                                        <div className="text-center p-3 text-muted">
                                            No income account entries recorded for this period.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* EXPENSES BREAKDOWN SECTION */}
                        <div className="expense-section">
                            <div className="expense-header" onClick={() => setShowExpenseDetails(!showExpenseDetails)}>
                                <div style={{ fontSize: '15px', fontWeight: '700', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <MdTrendingDown style={{ fontSize: '20px' }} />
                                    Expenses Breakdown ({from_date} to {to_date})
                                    <span style={{ fontSize: '13px', background: '#fef2f2', color: '#991b1b', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>
                                        Total Expenses: {formatCurrency(totalExpense)}
                                    </span>
                                </div>
                                <div style={{ color: '#991b1b' }}>
                                    {showExpenseDetails ? <MdExpandLess size={24} /> : <MdExpandMore size={24} />}
                                </div>
                            </div>

                            {showExpenseDetails && (
                                <div style={{ padding: '16px' }}>
                                    {expenseAccounts.length > 0 ? (
                                        <table className="w-100">
                                            <thead>
                                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                                    <th className="p-2 text-left">Expense Account / Category</th>
                                                    <th className="p-2 text-right">Amount (₹)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {expenseAccounts.map((exp) => (
                                                    <tr key={exp.accountguid} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td className="p-2">
                                                            <strong>{exp.name}</strong>
                                                            {exp.accountnumber && exp.accountnumber !== '-' && (
                                                                <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '8px' }}>
                                                                    ({exp.accountnumber})
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="p-2 text-right bold" style={{ color: '#dc2626' }}>
                                                            {formatCurrency(exp.balance)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr style={{ background: '#f1f5f9', borderTop: '2px solid #cbd5e1' }}>
                                                    <td className="p-2 bold">Total Period Expenses</td>
                                                    <td className="p-2 text-right bold" style={{ color: '#dc2626' }}>
                                                        {formatCurrency(totalExpense)}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    ) : (
                                        <div className="text-center p-3 text-muted">
                                            No expense account entries recorded for this period.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* POS RECEIPT SUMMARY BREAKDOWN */}
                        <div className="pos-section">
                            <div className="pos-header" onClick={() => setShowPosDetails(!showPosDetails)}>
                                <div style={{ fontWeight: '700', fontSize: '15px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <MdReceiptLong style={{ fontSize: '20px', color: '#16a34a' }} />
                                    POS Receipt Breakdown ({from_date} to {to_date})
                                    <span style={{ fontSize: '13px', background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>
                                        Total Credit: {formatCurrency(totalCredit)}
                                    </span>
                                </div>
                                <div>
                                    {showPosDetails ? <MdExpandLess size={24} /> : <MdExpandMore size={24} />}
                                </div>
                            </div>

                            {showPosDetails && (
                                <div style={{ padding: '16px' }}>
                                    {voucherSummary.length > 0 ? (
                                        <table className="w-100">
                                            <thead>
                                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                                    <th className="p-2 text-left">#</th>
                                                    <th className="p-2 text-left">Voucher Name</th>
                                                    <th className="p-2 text-left">Voucher Type</th>
                                                    <th className="p-2 text-right">Total Amount</th>
                                                    <th className="p-2 text-center">Transactions</th>
                                                    <th className="p-2 text-center">% of Total</th>
                                                    <th className="p-2 text-center">Unique IDs</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {voucherSummary.map((voucher, idx) => (
                                                    <tr key={voucher.voucher} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td className="p-2">{idx + 1}</td>
                                                        <td className="p-2 fcap">
                                                            <strong>{voucher.voucher}</strong>
                                                        </td>
                                                        <td className="p-2 fcap">{voucher.voucher_type}</td>
                                                        <td className="p-2 text-right bold">
                                                            {formatCurrency(voucher.total_amount)}
                                                        </td>
                                                        <td className="p-2 text-center">{voucher.count}</td>
                                                        <td className="p-2 text-center">
                                                            {grandTotalVouchers > 0 ? ((voucher.total_amount / grandTotalVouchers) * 100).toFixed(1) : 0}%
                                                        </td>
                                                        <td className="p-2 text-center">
                                                            <span className="badge">{voucher.voucherids.length}</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr style={{ background: '#f1f5f9', borderTop: '2px solid #cbd5e1' }}>
                                                    <td colSpan="3" className="p-2 bold">Total Receipts Summary</td>
                                                    <td className="p-2 text-right bold">{formatCurrency(grandTotalVouchers)}</td>
                                                    <td className="p-2 text-center bold">{totalTransactions}</td>
                                                    <td className="p-2 text-center bold">100%</td>
                                                    <td className="p-2 text-center bold">{totalUniqueIDs}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    ) : (
                                        <div className="text-center p-3 text-muted">
                                            No POS receipt transactions found for the selected date range.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}